import React, { useState } from 'react';
import {
  Box,
  Button,
  FormControlLabel,
  Grid2 as Grid,
  IconButton,
  InputAdornment,
  Switch,
  TextField,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';

const PartnerForm = ({ initialData, onSubmit, onCancel, buttonText = 'Submit', disabledFields = [] }) => {
  const [showPassword, setShowPassword] = useState(false);

  const validationSchema = Yup.object().shape({
    name: Yup.string().required('Partner name is required'),
    email: Yup.string().email('Invalid email format').required('Email is required'),
    password: Yup.string().min(6, 'Password must be at least 6 characters').required('Password is required'),
    is_embedded: Yup.boolean(),
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: initialData,
  });

  const handleClickShowPassword = () => {
    setShowPassword((prev) => !prev);
  };

  return (
    <Box sx={{ mt: 6 }}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={2}>
          <Grid size={6}>
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Partner Name"
                  fullWidth
                  placeholder="Enter Partner Name"
                  margin="normal"
                  size="small"
                  error={!!errors.name}
                  helperText={errors.name?.message}
                  disabled={disabledFields.includes('name')}
                />
              )}
            />
          </Grid>
          <Grid size={6}>
            <Controller
              name="email"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Email"
                  fullWidth
                  placeholder="Enter Email"
                  margin="normal"
                  size="small"
                  error={!!errors.email}
                  helperText={errors.email?.message}
                  disabled={disabledFields.includes('email')}
                />
              )}
            />
          </Grid>
          <Grid size={6}>
            <Controller
              name="password"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Password"
                  fullWidth
                  placeholder="Enter Password"
                  margin="normal"
                  size="small"
                  type={showPassword ? 'text' : 'password'}
                  error={!!errors.password}
                  helperText={errors.password?.message}
                  disabled={disabledFields.includes('password')}
                  SlotProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleClickShowPassword}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />
          </Grid>
          <Grid size={6} display="flex" alignItems="center">
            <FormControlLabel
              fullWidth
              control={
                <Controller
                  name="is_embedded"
                  control={control}
                  render={({ field }) => (
                    <Switch
                      {...field}
                      disabled={disabledFields.includes('is_embedded')}
                      checked={field.value}
                      label="Embedded"
                      fullWidth
                      margin="normal"
                      size="small"
                    />
                  )}
                />
              }
              label="Embedded Partner"
            />
          </Grid>
        </Grid>
        <Box
          sx={{
            float: 'right',
            mt: 2,
            right: 0,
          }}
        >
          <Button variant="outlined" color="info" sx={{ mr: 2 }} onClick={onCancel}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" color="primary">
            {buttonText}
          </Button>
        </Box>
      </form>
    </Box>
  );
};

export default PartnerForm;
